/**
 * Сценарий партнёра: какие экраны и в каком порядке.
 *
 * Раньше порядок был зашит в сторе одной константой под effective-records:
 * шесть отрезков полосы, первый — артисты. У другого заказчика артистов
 * нет вовсе, зато есть обложка, а отрезков пять. Пока порядок лежит в коде,
 * каждый новый заказчик добавляет в сторе ещё одно условие.
 *
 * Поэтому сценарий — данные. Экраны остаются общей библиотекой: добавить
 * заказчику шаг можно только тем экраном, который в библиотеке уже есть,
 * а новый экран пишется как новый тип и становится доступен всем. Условий
 * вида «если партнёр такой-то» в экранах быть не должно.
 */

export type Branch = 'ready' | 'custom';

export type FlowStep =
  | 'cover'
  | 'pet'
  | 'base'
  | 'done'
  | 'artists'
  | 'catalog'
  | 'shop'
  // Витрина СИЯЙ: категории, избранное, добавление прямо с карточки.
  | 'store'
  // Мастерская СИЯЙ: изделие, принт, цвет и размер на одном экране.
  | 'studio'
  // Карточка готовой вещи СИЯЙ: цвет, размер, состав.
  | 'item'
  | 'prints'
  | 'product'
  | 'builder'
  | 'library'
  | 'placement'
  | 'result'
  | 'order';

export interface Segment {
  label: string;
  steps: FlowStep[];
}

export interface FlowPlan {
  routes: Record<Branch, FlowStep[]>;
  /**
   * Шаги, которые занимают модуль целиком — без шапки и полосы шагов.
   *
   * Обложка именно такая: полоса шагов на ней показывала бы прогресс
   * там, где человек ещё ничего не начал, а шапка с корзиной предлагала
   * бы заглянуть в пустую корзину.
   */
  fullBleedSteps: FlowStep[];
  /**
   * Полоса шагов. Один отрезок — один экран, без вложенности: иначе
   * на первом экране зажигаются сразу два «текущих» шага.
   */
  segments: Segment[];
}

/** effective-records: витрина по артистам, конструктор поверх их принтов. */
const RECORDS: FlowPlan = {
  fullBleedSteps: [],
  routes: {
    ready: ['artists', 'catalog', 'prints', 'product', 'order'],
    custom: ['artists', 'catalog', 'prints', 'builder', 'library', 'placement', 'result', 'order'],
  },
  segments: [
    { label: 'артисты', steps: ['artists'] },
    { label: 'каталог', steps: ['catalog', 'prints'] },
    { label: 'основа', steps: ['builder', 'product'] },
    { label: 'принт', steps: ['library'] },
    { label: 'размещение', steps: ['placement', 'result'] },
    { label: 'заказ', steps: ['order'] },
  ],
};

/** ФОСИ: обложка, витрина, дальше либо готовое изделие, либо свой принт. */
const FOSY: FlowPlan = {
  fullBleedSteps: ['cover'],
  routes: {
    // Готовое изделие идёт сразу к основе: принт у него уже есть.
    // Своё — через конструктор: снимок, имя, год, и только потом основа.
    //
    // «order» — оформление и оплата прямо в виджете (brand.sitePayment):
    // форма доставки и касса здесь же, без мини-приложения.
    ready: ['cover', 'shop', 'base', 'done', 'order'],
    // Основа идёт первой и у своего принта: цвет футболки решает, каким
    // будет принт, и выбирать его после сборки значит собирать дважды.
    custom: ['cover', 'shop', 'base', 'pet', 'done', 'order'],
  },
  segments: [
    { label: 'товар', steps: ['cover', 'shop'] },
    { label: 'основа', steps: ['base'] },
    { label: 'принт', steps: ['pet'] },
    { label: 'размещение', steps: ['placement'] },
    { label: 'заказ', steps: ['done', 'order'] },
  ],
};

/**
 * ФОСИ в мини-приложении телеграма.
 *
 * Тот же сценарий без обложки и с оплатой в конце. Обложка объясняла,
 * что это за модуль на чужом сайте; в бот человек пришёл сам и объяснять
 * ему поздно. Зато появляется форма и деньги: здесь мы дома, и спросить
 * адрес можно, а на чужой странице — нет.
 */
const FOSY_MINIAPP: FlowPlan = {
  fullBleedSteps: [],
  routes: {
    ready: ['shop', 'base', 'done', 'order'],
    custom: ['shop', 'base', 'pet', 'done', 'order'],
  },
  segments: [
    { label: 'товар', steps: ['shop'] },
    { label: 'основа', steps: ['base'] },
    { label: 'принт', steps: ['pet'] },
    { label: 'заказ', steps: ['done', 'order'] },
  ],
};

/**
 * СИЯЙ в своём боте.
 *
 * Два пути с одной витрины. Готовое изделие человек кладёт в корзину
 * прямо с карточки и идёт оформлять — ни цвета, ни принта выбирать
 * не нужно, вещь уже собрана. Своё собирается в мастерской: один экран,
 * где сразу и изделие, и принт, и цвет, и размер.
 *
 * Мастерская одним экраном, а не четырьмя шагами, — это главное отличие
 * от остальных заказчиков, и оно из макета. У ФОСИ сборка разнесена
 * по шагам, потому что там каждый шаг — отдельный разговор: сначала
 * снимок питомца, потом надпись. Здесь выбирать нечего, кроме принта
 * и цвета, и разносить это по экранам значило бы растянуть минутное
 * дело на четыре перехода.
 */
const SIYAI_MINIAPP: FlowPlan = {
  fullBleedSteps: [],
  routes: {
    // Готовая вещь открывается карточкой, а не уезжает сразу в заказ.
    //
    // Шага `product` здесь не было, и нажатие на товар на витрине
    // вело прямиком к составу заказа: человек, ткнувший в футболку
    // посмотреть, оказывался на оформлении вещи, которую не выбирал
    // ни по цвету, ни по размеру.
    ready: ['store', 'item', 'done', 'order'],
    custom: ['store', 'studio', 'done', 'order'],
  },
  segments: [
    { label: 'каталог', steps: ['store'] },
    { label: 'товар', steps: ['item'] },
    { label: 'дизайн', steps: ['studio'] },
    { label: 'заказ', steps: ['done', 'order'] },
  ],
};

const PLANS: Record<string, FlowPlan> = {
  fosy: FOSY,

  // На сайте СИЯЙ пока не стоит: бот — единственная поверхность.
  // Строка появится, когда понадобится блок на странице.
  'siyai@miniapp': SIYAI_MINIAPP,

  // Поверхность — часть ключа, а не условие внутри сценария. Один и тот же
  // заказчик живёт на сайте и в телеграме, и пути там разные по существу,
  // а не в мелочах.
  'fosy@miniapp': FOSY_MINIAPP,
};

/** Где открыт виджет: блоком на сайте или мини-приложением в телеграме. */
export type Surface = 'site' | 'miniapp';

/**
 * Сценарий по слагу партнёра и поверхности.
 *
 * Сначала ищется сценарий для этой поверхности, потом общий сценарий
 * заказчика, и только потом — effective-records как старый порядок. Так
 * подключение новой поверхности не может сломать работающую: пока её
 * строки нет, всё остаётся как было.
 */
export function planFor(slug: string, surface: Surface = 'site'): FlowPlan {
  if (surface !== 'site') {
    const scoped = PLANS[`${slug}@${surface}`];
    if (scoped) return scoped;
  }

  return PLANS[slug] ?? RECORDS;
}
